import PropTypes from 'prop-types';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useEffect, useState, useRef } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { m } from 'framer-motion';
// use @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, IconButton, Avatar, Switch } from '@mui/material';
// use routes
import { snapshotLastPractices, restoreLastPractices } from 'src/utils/lastPractice';
import { PATH_DASHBOARD, PATH_AUTH } from '../../../routes/paths';
// use hooks
import { useAuth, useIsMountedRef } from '../../../hooks';
// use components
import { MenuPopover } from '../../../components';
import { useSettingsContext } from '../../../components/settings/context';
import BadgeDot from '../../../components/settings/drawer/BadgeDot';
import ContactsPopover from './ContactsPopover';
import { DND_EVENT, readDnd, writeDnd } from '../../../components/callPill/callPillSync';
import { isCallPillEnabled } from '../../../components/callPill/callPillFlag';
import { useOutbound } from '../../../components/outbound/OutboundCallProvider';
import { myDeskLabel } from '../../../components/outbound/MyDeskChip';
// redux
import { dispatch } from '../../../redux/store';
import { updatePracticeId } from '../../../redux/slices/practiceTreatmentList';
// utils
import clearStore from '../../../utils/clearStore';
import capitalizeFirstLetter from '../../../utils/capitalizedFunc';

// ----------------------------------------------------------

const MENU_OPTIONS = [
    {
        label: 'Profile',
        linkTo: PATH_DASHBOARD.user.profile,
    }
];

// ----------------------------------------------------------

export default function AccountPopover({ showSwitchUser = false }) {

    const navigate = useNavigate();

    const { user, logout } = useAuth();

    const [open, setOpen] = useState(null);

    const isMountedRef = useIsMountedRef();

    const { enqueueSnackbar } = useSnackbar();

    // Theme/display settings drawer — its floating bottom-right FAB was removed
    // (2026-07-04); the drawer now opens from this menu instead.
    const settings = useSettingsContext();

    // Switch-User (2026-07-04): the header's standalone users-group icon moved into this
    // menu. The popover itself still lives in ContactsPopover, rendered here in
    // controlled mode and anchored to the avatar button.
    const avatarBtnRef = useRef(null);
    const [switchUserAnchor, setSwitchUserAnchor] = useState(null);

    // Call pill "Do not disturb": per user, per computer; the pill and other
    // tabs pick the change up from the same storage key.
    const dndUserId = user?.pk;
    const [callDnd, setCallDnd] = useState(() => readDnd(dndUserId));
    // My desk (CU-869fmcc9r): null outside the outbound provider.
    const outbound = useOutbound();
    useEffect(() => {
        setCallDnd(readDnd(dndUserId));
        const onDnd = (e) => { if (String(e?.detail?.userId) === String(dndUserId)) setCallDnd(Boolean(e.detail.on)); };
        window.addEventListener(DND_EVENT, onDnd);
        return () => window.removeEventListener(DND_EVENT, onDnd);
    }, [dndUserId]);

    const handleOpen = (event) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            dispatch(updatePracticeId(null));
            localStorage.removeItem('practiceId');
            localStorage.removeItem('isAdmin');
            localStorage.removeItem('dateFormat');
            // Keep per-user last-practice memory through the full wipe so the next
            // sign-in reopens the practice this user last worked in.
            const keepLastPractices = snapshotLastPractices();
            localStorage.clear();
            restoreLastPractices(keepLastPractices);
            navigate(PATH_AUTH.login, { replace: true });
            clearStore();
            if (isMountedRef.current) {
                handleClose();
            }
        } catch (error) {
            enqueueSnackbar('Unable to logout!', {
                variant: 'error', anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'center'
                }
            },
            );
        }
    };

    return (
        <>
            <IconButton
                ref={avatarBtnRef}
                onClick={handleOpen}
                component={m.button}
                whileTap="tap"
                whileHover="hover"
                sx={{
                    width: 40,
                    height: 40,
                    background: (theme) => alpha(theme.palette.grey[500], 0.08),
                    ...(open && {
                        background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    }),
                }}
                id='account-popover'
            >
                <Avatar
                    src={user?.avatar}
                    alt={user?.first_name}
                    sx={{
                        width: 36,
                        height: 36,
                        border: (theme) => `solid 2px ${theme.palette.background.default}`,
                    }}
                />
            </IconButton>


            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                id='account-menu-popover'
                sx={{
                    p: 0,
                    mt: 1.5,
                    ml: 0.75,
                    '& .MuiMenuItem-root': {
                        typography: 'body2',
                        borderRadius: 0.75,
                    },
                }}
            >
                <Box sx={{ my: 1.5, px: 2.5 }}>
                    <Typography variant="subtitle2" noWrap>
                        {user?.salutation && `${capitalizeFirstLetter(user?.salutation)}.`} {user?.first_name} {user?.last_name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                        {user?.email}
                    </Typography>
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <Stack sx={{ p: 1 }}>
                    {MENU_OPTIONS.map((option) => (
                        <MenuItem
                            key={option.label}
                            id={option.label}
                            to={option.linkTo}
                            component={RouterLink}
                            onClick={handleClose}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                    {showSwitchUser && (
                        <MenuItem
                            id='switch-user'
                            onClick={() => {
                                handleClose();
                                setSwitchUserAnchor(avatarBtnRef.current);
                            }}
                        >
                            Switch user
                        </MenuItem>
                    )}
                    {outbound?.canPickDesk && (
                        <MenuItem
                            id='my-desk'
                            onClick={() => {
                                handleClose();
                                outbound.openDeskPicker();
                            }}
                        >
                            {myDeskLabel(outbound.myDesk)}
                        </MenuItem>
                    )}
                    {dndUserId != null && isCallPillEnabled() && (
                        <MenuItem
                            id='call-do-not-disturb'
                            role='menuitemcheckbox'
                            aria-checked={callDnd}
                            onClick={() => writeDnd(dndUserId, !callDnd)}
                            sx={{ justifyContent: 'space-between' }}
                        >
                            Do not disturb (calls)
                            <Switch size="small" checked={callDnd} tabIndex={-1} inputProps={{ 'aria-hidden': true, tabIndex: -1 }} sx={{ pointerEvents: 'none' }} />
                        </MenuItem>
                    )}
                    <MenuItem
                        id='theme-settings'
                        onClick={() => {
                            handleClose();
                            settings.onToggle();
                        }}
                    >
                        Theme settings
                        {/* The removed settings FAB showed this dot when theme settings
                            differ from defaults (canReset) — kept on its new home. */}
                        {settings.canReset && (
                            <BadgeDot sx={{ position: 'static', ml: 1 }} />
                        )}
                    </MenuItem>
                </Stack>

                <Divider sx={{ borderStyle: 'dashed' }} />

                <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
                    Logout
                </MenuItem>
            </MenuPopover>

            {showSwitchUser && (
                <ContactsPopover
                    externalControl={{
                        anchorEl: switchUserAnchor,
                        open: Boolean(switchUserAnchor),
                        onClose: () => setSwitchUserAnchor(null),
                    }}
                />
            )}
        </>
    );
}

AccountPopover.propTypes = {
    showSwitchUser: PropTypes.bool,
};
